import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Input, Select, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Menubar from "../../../components/WhrManager/App";
import { http } from "../../../services/http";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Navbar from "../../../components/navbar";
import ReturnRequestModal from "../../../components/WhrManager/ReturnRequestModal";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";

const PAGE_SIZE = 20;
const today = moment().format("YYYY-MM-DD");

const ReturnsList = () => {
  const { t } = useTranslation();
  const fetchIdRef = useRef(0);
  const { warehouse } = useSelector((state) => state.main);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const [selectedRow, setSelectedRow] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [returnReasons, setReturnReasons] = useState([]);

  const [filters, setFilters] = useState({
    docNum: "",
    cardSearch: "",
    returnStatus: "",
    returnReason: "",
    cancelled: "",
    docStatus: "",
    whsCode: `${warehouse},shr`,
    startDate: "",
    endDate: today,
  });

  // Fetch dynamic return reasons for the filter select
  useEffect(() => {
    http
      .get("api/returnbyelyor/return-reasons")
      .then(({ data }) => {
        const opts = [
          { label: t("all"), value: "" },
          ...(data ?? []).map((r) => ({ label: r.name, value: r.code })),
        ];
        setReturnReasons(opts);
      })
      .catch(console.error);
  }, [t]);

  const fetchData = useCallback(
    async (page) => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);

      try {
        const params = new URLSearchParams({
          pageSize: PAGE_SIZE,
          skip: (page - 1) * PAGE_SIZE,
          canceled: "N",
        });
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== "") params.append(k, v);
        });

        const { data } = await http.get(
          `api/returnbyelyor/returns?${params.toString()}`,
        );

        if (fetchId !== fetchIdRef.current) return;
        const docs = data?.docs ?? [];
        setTotal(data?.total ?? 0);
        setHasMore(docs.length === PAGE_SIZE);
        setDataSource(docs);
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        console.error(err);
        setHasMore(false);
      } finally {
        if (fetchId === fetchIdRef.current) setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  const setFilter = useCallback((key, value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const debouncedSetFilter = useMemo(
    () => debounce((key, value) => setFilter(key, value), 500),
    [setFilter],
  );

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => {
    if (hasMore) setCurrentPage((p) => p + 1);
  };

  const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n ?? 0);

  const textFilter = (filterKey) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={t("search")}
          value={selectedKeys[0] ?? ""}
          onChange={(e) => {
            setSelectedKeys([e.target.value]);
            debouncedSetFilter(filterKey, e.target.value);
          }}
          onPressEnter={() => confirm()}
          style={{ width: 180 }}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const selectFilter = (filterKey, options) => ({
    filterDropdown: ({ setSelectedKeys, confirm }) => (
      <div style={{ padding: 8 }}>
        <Select
          value={filters[filterKey] ?? ""}
          onChange={(v) => {
            setSelectedKeys([v]);
            setFilter(filterKey, v);
            confirm();
          }}
          style={{ width: 180 }}
          options={options}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const dateRangeFilter = () => ({
    filterDropdown: ({ confirm }) => (
      <div
        style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6 }}
      >
        <div>
          <p style={{ margin: "0 0 2px", fontSize: 12 }}>{t("startDate")}</p>
          <Input
            type="date"
            value={filters.startDate}
            max={filters.endDate || today}
            style={{ width: 160 }}
            onChange={(e) => setFilter("startDate", e.target.value)}
          />
        </div>
        <div>
          <p style={{ margin: "0 0 2px", fontSize: 12 }}>{t("endDate")}</p>
          <Input
            type="date"
            value={filters.endDate}
            min={filters.startDate}
            max={today}
            style={{ width: 160 }}
            onChange={(e) => setFilter("endDate", e.target.value)}
          />
        </div>
        <Button size="small" onClick={() => confirm()} style={{ marginTop: 4 }}>
          OK
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const columns = useMemo(
    () => [
      {
        title: t("docNum"),
        dataIndex: "docNum",
        key: "docNum",
        width: 100,
        ...textFilter("docNum"),
      },
      {
        title: t("status"),
        dataIndex: "documentStatus",
        key: "documentStatus",
        width: 90,
        render: (v) =>
          v === "O" ? t("open") : v === "C" ? t("closed") : v ?? "-",
        ...selectFilter("docStatus", [
          { value: "", label: t("all") },
          { value: "O", label: t("open") },
          { value: "C", label: t("closed") },
        ]),
      },
      {
        title: t("docDate"),
        dataIndex: "docDate",
        key: "docDate",
        width: 120,
        render: (v) => (v ? moment(v).format("DD.MM.YYYY") : "-"),
        ...dateRangeFilter(),
      },
      {
        title: t("cardCode"),
        dataIndex: "cardCode",
        key: "cardCode",
        width: 110,
      },
      {
        title: t("cardName"),
        dataIndex: "cardName",
        key: "cardName",
        width: 180,
        ...textFilter("cardSearch"),
      },
      {
        title: t("comments"),
        dataIndex: "comments",
        key: "comments",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("currency"),
        dataIndex: "docCurrency",
        key: "docCurrency",
        width: 80,
      },
      {
        title: t("docTotal"),
        dataIndex: "docTotal",
        key: "docTotal",
        width: 130,
        render: (v, r) =>
          r.docCurrency === "UZS"
            ? `${fmt(r.docTotalFc)} UZS`
            : `${fmt(v)} ${r.docCurrency}`,
      },
      {
        title: t("docTotalFc"),
        dataIndex: "docTotalFc",
        key: "docTotalFc",
        width: 130,
        render: (v, r) => `${fmt(v)} ${r.docCurrency}`,
      },
      {
        title: t("slpCode"),
        dataIndex: "salesPersonCode",
        key: "salesPersonCode",
        width: 90,
      },
      {
        title: t("slpName"),
        dataIndex: "salesPersonName",
        key: "salesPersonName",
        width: 160,
      },
      {
        title: t("weight"),
        dataIndex: "weight",
        key: "weight",
        width: 90,
        render: (v) => fmt(v),
      },
      {
        title: t("volume"),
        dataIndex: "volume",
        key: "volume",
        width: 90,
        render: (v) => fmt(v),
      },
      {
        title: t("discSum"),
        dataIndex: "discSum",
        key: "discSum",
        width: 110,
        render: (v) => fmt(v),
      },
      {
        title: t("discSumFc"),
        dataIndex: "discSumFC",
        key: "discSumFC",
        width: 120,
        render: (v) => fmt(v),
      },
      {
        title: t("returnReason"),
        dataIndex: "returnReasonName",
        key: "returnReasonName",
        width: 160,
        render: (v, r) => v ?? r.returnReason ?? "-",
        ...selectFilter("returnReason", returnReasons),
      },
      {
        title: t("returnStatus"),
        dataIndex: "returnStatusName",
        key: "returnStatusName",
        width: 160,
        render: (v, r) => v ?? r.returnStatus ?? "-",
        ...selectFilter("returnStatus", [
          { label: t("all"), value: "" },
          { label: t("tasdiqlanmagan"), value: "Tasdiqlanmagan" },
          { label: t("tasdiqlangan"), value: "Tasdiqlangan" },
        ]),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, filters, returnReasons],
  );

  return (
    <div className="flex h-full w-full overflow-y-auto">
      <Menubar />

      <div className="h-screen w-full overflow-y-auto">
        <Navbar />

        <h1 className="font-poppins ml-4 mt-10 text-xl font-bold text-black sm:ml-5 sm:mt-14 sm:text-2xl">
          {t("returns")}
        </h1>

        <div className="mt-4 w-full border border-[#E8E8E8]" />

        <div className="mb-4 flex flex-col justify-between sm:flex-row">
          <div className="pl-5 pt-5">
            <span className="font-bold">
              {t("page")}: {currentPage}
            </span>
            {/* <span className="ml-4 text-sm text-gray-500">
              {t("total")}: {fmt(total)}
            </span> */}
          </div>
          <div className="mt-2 flex gap-2 sm:mr-10">
            <Button
              disabled={currentPage === 1}
              onClick={handlePrevPage}
              className="h-[40px] w-[120px] rounded-2xl bg-gray-300 text-gray-700"
            >
              {t("previous")}
            </Button>
            <Button
              disabled={!hasMore}
              onClick={handleNextPage}
              className="h-[40px] w-[120px] rounded-2xl bg-[#0A4D68] text-white"
            >
              {t("next")}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto px-2 pb-6">
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey={(r) => r.docEntry}
            pagination={false}
            loading={loading}
            scroll={{ x: 1800 }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRow(record);
                setModalVisible(true);
              },
              style: { cursor: "pointer" },
            })}
          />
        </div>
      </div>

      <ReturnRequestModal
        isShowDrivers
        visible={modalVisible}
        data={selectedRow}
        onClose={() => setModalVisible(false)}
        onSaved={() => {
          setModalVisible(false);
          fetchData(currentPage);
        }}
      />
    </div>
  );
};

export default ReturnsList;
